<template>
  <div>
    <!-- show loader whilst retrieving information for the component -->
    <div v-if="!loaded_timesheet_auth" class="row">
      <div class="col-12">
        <order_loader :loadingMessage="'Accounts & Timesheet Auth'"></order_loader>
      </div>
    </div>
    <!-- once component data loaded remove loader -->
    <div v-else>
      <ValidationObserver ref="orderTimesheetsAccountsForm" slim v-if="isPartialOrderComplete">
        <form role="form" @submit.prevent="order_timesheets_accounts_submit" class="row">
          <!-- Accounts Information -->
          <div class="col-12">
            <b-card header="Accounts Information" header-tag="h4" body-class="mb-3 pb-2 pt-5">
              <div class="row">
                <div class="col-12">
                  <button @click.prevent="add_address_modal" class="btn orcaBtn blueBtn sm_btn float-right mb-3">Add client address</button>
                </div>
                <!-- Invoice Address -->
                <ValidationProvider rules="required" v-slot="{errors, classes}" name="Invoice Address" tag="div" class="col-xl-6 form-group">
                  <label class="form-label" for="invoice_address">Invoice Address</label>
                  <multiselect v-model="timesheet_accounts.invoice_address" id="invoice_address" class="requiredField" :class="classes"
                               name="invoice_address" placeholder="" label="full" :options="ms_opts.invoice_address"></multiselect>
                  <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
                </ValidationProvider>
                <!-- Days to pay from invoice -->
                <ValidationProvider rules="required|decimal" v-slot="{errors, classes}" name="Payment Terms - Days from Invoice" tag="div" class="col-xl-6 form-group">
                  <label class="form-label" for="days_to_pay_from_invoice">Payment Terms - Days from Invoice</label>
                  <input type="text" v-model="timesheet_accounts.days_to_pay_from_invoice" id="days_to_pay_from_invoice" name="days_to_pay_from_invoice"
                         class="form-input requiredField" :class="classes">
                  <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
                </ValidationProvider>
              </div>
              <!-- Accounts Contact -->
              <div class="row">
                <div class="col-12">
                  <hr>
                </div>
                <div class="col-6">
                  <h4>Accounts Contact</h4>
                </div>
                <!-- add client contact - open modal button -->
                <div class="col-6">
                  <button @click.prevent="add_contact" class="btn orcaBtn blueBtn sm_btn float-right mb-3">Add client contact</button>
                </div>
                <!-- Accounts Contact multiselect -->
                <ValidationProvider rules="required" v-slot="{errors, classes}" name="Accounts Contact" tag="div" class="basic_order_info col-xl-6 form-group">
                  <label class="form-label" for="accounts_contact">Accounts Contact</label>
                  <multiselect v-model="timesheet_accounts.accounts_contact" placeholder="" id="accounts_contact" track-by="id" :options="ms_opts.accounts_contact"
                               :option-height="200" :show-labels="false" class="requiredField" :class="classes">
                    <template slot="singleLabel" slot-scope="props">
                        <span class="option__desc_single">
                          <span class="option__title">{{ multiselect_accounts_contact_full_name(props) }}</span>
                        </span>
                    </template>
                    <template slot="option" slot-scope="props">
                      <div class="option__desc">
                        <span class="option__title">{{ multiselect_accounts_contact_full_name(props) }}</span>
                        <span class="option__small" v-if="has_job_title_multiselect_accounts_contact(props)">{{ multiselect_accounts_contact_job_title(props) }}</span>
                      </div>
                    </template>
                  </multiselect>
                  <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
                </ValidationProvider>
                <!-- Accounts Contact: Job Title -->
                <div class="col-xl-6 form-group">
                  <label class="form-label" for="accounts_contact_job_title">Accounts Contact: Job Title</label>
                  <input v-if="has_job_title_accounts_contact" type="text" class="form-input disabled" readonly
                         v-model="timesheet_accounts.accounts_contact.rel_profile.job_title.job_title" id="accounts_contact_job_title" name="accounts_contact_job_title">
                  <input v-else type="text" id="null_accounts_contact_job_title" name="null_accounts_contact_job_title" class="form-input disabled" readonly>
                </div>
                <!-- Accounts Contact: Mobile -->
                <div class="col-xl-3 form-group">
                  <label class="form-label" for="accounts_contact_mobile">Accounts Contact: Mobile</label>
                  <input v-if="accounts_contact_selected" type="text" v-model="timesheet_accounts.accounts_contact.phones.work_mobile"
                         id="accounts_contact_mobile" name="accounts_contact_mobile" class="form-input disabled" readonly>
                  <input v-else type="text" id="null_accounts_contact_mobile" name="null_accounts_contact_mobile" class="form-input disabled" readonly>
                </div>
                <!-- Accounts Contact: Landline Phone -->
                <div class="col-xl-3 form-group">
                  <label class="form-label" for="accounts_contact_phone">Accounts Contact: Landline Phone</label>
                  <input v-if="accounts_contact_selected" type="text" v-model="timesheet_accounts.accounts_contact.phones.work_landline"
                         id="accounts_contact_phone" name="accounts_contact_phone" class="form-input disabled" readonly>
                  <input v-else type="text" id="null_accounts_contact_phone" name="null_accounts_contact_phone" class="form-input disabled" readonly>
                </div>
                <!-- Accounts Contact: Email -->
                <ValidationProvider rules="required" v-slot="{errors, classes}" name="Accounts Contact: Email" tag="div" class="col-xl-6 form-group">
                  <label class="form-label" for="accounts_contact_email">Accounts Contact: Email</label>
                  <input v-if="accounts_contact_selected" type="text" v-model="timesheet_accounts.accounts_contact.emails.work_email" id="accounts_contact_email"
                         :class="classes" name="accounts_contact_email" class="form-input disabled" readonly>
                  <input v-else type="text" id="null_accounts_contact_email" name="null_accounts_contact_email" class="form-input disabled" :class="classes" readonly>
                  <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
                </ValidationProvider>
              </div>
            </b-card>
          </div>
          <!-- Timesheet Authorisation -->
          <div class="col-12">
            <b-card header="Timesheet Authorisation" header-tag="h4" body-class="mb-3 pb-2 pt-5">
              <div class="row">
                <div class="col-12">
                  <h4>Timesheet Contact Authorisation & Submission</h4>
                </div>
                <!-- select to use default or order specific timesheet authorisation -->
                <div class="col-12">
                  <div class="row">
                    <div class="col-12">
                      <p class="font-weight-bold pt-0 form-group bp_color">Select from the following to set the Timesheet Client Contact Authorisation procedure</p>
                    </div>
                  </div>
                  <ValidationProvider rules="required|oneOf:Default,Order" v-slot="{errors, classes}" name="Timesheet Submission Authorisation Type" tag="div" class="row">
                    <div class="col-xl-4 form-group">
                      <div class="pretty p-default p-curve">
                        <input type="radio" name="ts_submission_auth_type" value="Default" v-model="timesheet_accounts.ts_submission_auth_type">
                        <div class="state"><label>Client Default Timesheet Authorisation</label></div>
                      </div>
                    </div>
                    <div class="col-xl-4 form-group">
                      <div class="pretty p-default p-curve">
                        <input type="radio" name="ts_submission_auth_type" value="Order" v-model="timesheet_accounts.ts_submission_auth_type">
                        <div class="state"><label>Timesheet Authorisation Per Job Order</label></div>
                      </div>
                    </div>

                    <div v-if="errors[0]" class="vvERROR col-12">{{ errors[0] }}</div>
                  </ValidationProvider>
                </div>
                <!-- if default timesheet authorisation selected - just display the company default timesheet authorisation contacts - do not allow change -->
                <div class="col-12" v-if="notEmptyObject(default_timesheet_auth) && isDefaultSubmissionType">
                  <div class="row">
                    <div class="col-12 form-group">
                      <hr class="mt-0">
                      <h4>Default Client Timesheet Authorisation and Submission Contacts</h4>
                    </div>
                    <div class="col-12">
                      <h5 class="bp_color">Contacts with permission to Submit Timesheet Instructions to <span v-if="crmType === 'payroll'">Genius Money</span><span
                          v-else-if="crmType === 'recruitment'">V3 Recruitment</span></h5>
                    </div>
                    <!-- list company default timesheet submitters -->
                    <div class="col-xl-4 form-group" v-for="ts_as in default_timesheet_auth" v-if="ts_as.submission_level >= 8">
                      <h5 class="font-weight-bold pt-0 mb-0">{{ default_ts_as_contact_full_name(ts_as) }}</h5>
                      <p class="b3_color pt-0 mb-0">{{ default_ts_as_contact_job_title(ts_as) }}</p>
                    </div>
                    <!-- only show if there are authorisers in the company default timesheet authorisation list -->
                    <div class="col-12 form-group" v-if="isDefaultSubmissionAuthorisation">
                      <hr class="mt-0">
                      <h5 class="bp_color">Contacts with permission to Authorise Timesheet Instructions</h5>
                    </div>
                    <!-- list company default timesheet authorisers (if there are any) -->
                    <div class="col-12 form-group" v-for="ts_as in default_timesheet_auth" v-if="ts_as.submission_level >= 2 && ts_as.submission_level <= 7">
                      <div class="row">
                        <div class="col-xl-4"><strong class="cardLabel b3_color">Authorising Contact Level {{ default_ts_as_submission_level(ts_as) }}</strong></div>
                        <div class="col-xl-4">
                          <h5 class="font-weight-bold pt-0 mb-0 cardLabel">{{ default_ts_as_contact_full_name(ts_as) }}</h5>
                        </div>
                        <div class="col-xl-4">
                          <p class="b3_color pt-0 mb-0 cardLabel b4_color">{{ default_ts_as_contact_job_title(ts_as) }}</p>
                        </div>
                      </div>
                    </div>
                    <div class="col-12">
                      <p>If you wish to change this order to custom timesheet authorisation, do so by viewing the order once it has been raised.</p>
                    </div>
                  </div>
                </div>
                <!-- if order specific timesheet authorisation selected - pre-load company default timesheet authorisation information into a form that is editable
                to select timesheet authorisation contacts specific to this order -->
                <div class="col-12" v-if="isOrderSubmissionType">
                  <ValidationObserver ref="editTSauthForm" slim>
                    <form role="form" @submit.prevent="">

                      <div class="row">

                        <!-- Order specific -- timesheet submitters -->
                        <div class="col-12">
                          <hr class="mt-0">
                          <h5 class="bp_color">Contacts with permission to Submit Timesheet Instructions to <span v-if="crmType === 'payroll'">Genius Money</span><span
                              v-else-if="crmType === 'recruitment'">V3 Recruitment</span></h5>
                        </div>

                        <div v-for="(c, i) in ts_auth.submit_ts" class="col-xl-4 form-group ts_auth_submit" :class="{ first: i===0 }">
                          <ValidationProvider rules="required" v-slot="{errors, classes}" :name="'Submitting Contact' + (i + 1)" tag="div" class="ts_auth_submit_multi">
                            <label class="form-label" :for="'submit_ts_contact_' + i">Submitting Contact {{ i + 1 }}</label>
                            <multiselect v-model="c.contact" placeholder="" :id="'submit_ts_contact_' + i" track-by="contact_id" :options="ms_opts.ts_contacts"
                                         :option-height="200" :show-labels="false" class="requiredField" :class="classes" @input="select_ts_contact">
                              <template slot="singleLabel" slot-scope="props">
                              <span class="option__desc_single">
                                   <span class="option__title">{{ props.option.person.first_name }} {{ props.option.person.surname }}</span>
                              </span>
                              </template>
                              <template slot="option" slot-scope="props">
                                <div class="option__desc">
                                  <span class="option__title">{{ props.option.person.first_name }} {{ props.option.person.surname }}</span>
                                  <span class="option__small" v-if="props.option.rel_profile.job_title_id !== null">
                                  {{ props.option.rel_profile.job_title.job_title }}
                                </span>
                                </div>
                              </template>
                            </multiselect>
                            <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
                          </ValidationProvider>

                          <button @click.prevent="remove_ts_auth_submitting_contact('Submitting Contact ' + (i + 1), i, c.id)"
                                  class="btn orcaBtn danger text-center float-right" v-if="i !== 0">X
                          </button>
                        </div>
                        <div v-if="ts_auth.submit_ts.length < 3 && ms_opts.ts_contacts.length > 0 && check_submit_ts_contact_null" class="col-xl-4">
                          <button @click.prevent="add_ts_auth_submitting_contact" class="btn orcaBtn">Add Submitting Contact</button>
                        </div>

                        <!-- Order specific -- timesheet authorisers -->
                        <div class="col-12">
                          <hr class="mt-0">
                          <h5 class="bp_color">Contacts with permission to Authorise Timesheet Instructions</h5>
                        </div>

                        <div v-for="(c, i) in ts_auth.authorising_ts" class="col-12 form-group">
                          <div class="row">
                            <div class="col-xl-5">
                              <p class="cardLabel pt-0">Authorising Contact Level {{ i + 1 }}</p>
                            </div>
                            <div class="col-xl-4 form-group">
                              <ValidationProvider rules="" v-slot="{errors, classes}" :name="'Authorising Contact' + (i + 1)" tag="div" class="ts_auth_submit_multi">
                                <label class="form-label" :for="'authorise_ts_contact_' + i">Authorising Contact {{ i + 1 }}</label>
                                <multiselect v-model="c.contact" placeholder="" :id="'authorise_ts_contact_' + i" track-by="id" :options="ms_opts.ts_contacts"
                                             :option-height="200" :show-labels="false" class="requiredField" :class="classes"
                                             @input="select_ts_contact">
                                  <template slot="singleLabel" slot-scope="props">
                              <span class="option__desc_single">
                                 <span class="option__desc_single">
                                   <span class="option__title">{{ props.option.person.first_name }} {{ props.option.person.surname }}</span>
                                 </span>
                              </span>
                                  </template>
                                  <template slot="option" slot-scope="props">
                                    <div class="option__desc">
                                      <span class="option__title">{{ props.option.person.first_name }} {{ props.option.person.surname }}</span>
                                      <span class="option__small" v-if="props.option.rel_profile.job_title_id !== null">
                                  {{ props.option.rel_profile.job_title.job_title }}
                                </span>
                                    </div>
                                  </template>
                                </multiselect>
                                <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
                              </ValidationProvider>
                            </div>
                            <div class="col-xl-3 form-group">
                              <button @click.prevent="remove_ts_auth_authorising_contact('Authorising Contact ' + (i + 1), i, c.id)" class="btn orcaBtn danger">
                                Remove
                              </button>
                            </div>
                          </div>
                        </div>

                        <div class="col-12" v-if="ts_auth.authorising_ts.length < 6 && ms_opts.ts_contacts.length > 0 && check_submit_ts_contact_null">
                          <div class="row">
                            <div class="col-xl-5">
                              <p class="cardLabel pt-0">Authorising Contact Level {{ ts_auth.authorising_ts.length + 1 }}</p>
                            </div>
                            <div class="col-xl-4 form-group">
                              <button @click.prevent="add_ts_auth_authorising_contact" class="btn orcaBtn">Add Authorising Contact</button>
                            </div>
                          </div>
                        </div>

                        <div class="col-12">
                          <p>To further customise the timesheet authentication process, do so by viewing the order once it has been raised.</p>
                        </div>
                      </div>

                    </form>
                  </ValidationObserver>
                </div>
              </div>
            </b-card>
          </div>
          <!-- save button -->
          <div class="mt-2 col-xl-6 offset-xl-6">
            <button class="btn orcaBtn w-100" type="submit">Save Timesheet Auth & Accounts</button>
          </div>
        </form>
      </ValidationObserver>
      <div v-else class="row">
        <div class="col-12">
          <h5 class="red_color">You must complete all previous sections first before filling out Timesheet Authorisation & Accounts Information.</h5>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";
import order_loader from "loaders/order_loader";

export default {
  name: "timesheet_authorisation",
  components: {
    Multiselect,
    order_loader
  },
  data() {
    return {
      loaded_timesheet_auth: false, // loader

      can_watch_auth_type:false,

      contacts: [], // original array of company contacts

      ms_opts: {
        invoice_address: [], // invoice address options
        accounts_contact: [], // accounts contact options

        ts_contacts: [], // contacts array for timesheet authorisations
      },
      default_timesheet_auth: [], // array of company default timesheet authorisation contacts

      positions: [], // REMOVE ...?

      // order specific timesheet authorisation data
      ts_auth: {
        authorising_ts: [],
        submit_ts: [
          {contact: null}
        ]
      },

      /*
       * object containing information to save
       */
      timesheet_accounts: {
        invoice_address: null, // selected invoice address
        days_to_pay_from_invoice: '', // selected payment terms - days from invoice
        accounts_contact: null, // selected accounts contact

        ts_submission_auth_type: 'Default', // whether Default or Order specific timesheet authorisation.

        timesheet_auth: [{}], // timesheet authorisation for the order - either company default or order specific
      }
    }
  },
  beforeMount() {
    // load data for component
    this.load_data().then(() => this.loaded_timesheet_auth = true);
  },
  computed: {
    /**
     * check if an order has been partially completed - if you get to this component then this should always return true
     * @returns {boolean}
     */
    isPartialOrderComplete() {
      return this.$store.state.orders.order_steps.positions_rates.partial_order_complete
    },
    /*
     * ******************************************
     * Invoice Address
     * ******************************************
     */
    /**
     * Trigger ms_opts.invoice_address array reload - from adding an address in the 'add client address' modal
     * @returns {boolean|*}
     */
    address_reload() {
      return this.$store.state.addresses.reload_addresses;
    },
    /*
     * ******************************************
     * Accounts contact
     * ******************************************
     */
    /**
     * check if an accounts contact has been selected and whether they have a job title
     * @returns {boolean}
     */
    has_job_title_accounts_contact() {
      return this.timesheet_accounts.accounts_contact !== null ? this.timesheet_accounts.accounts_contact.rel_profile.job_title_id !== null : false;
    },
    /**
     * check if an accounts contact has been selected from the multiselect
     * @returns {boolean}
     */
    accounts_contact_selected() {
      return this.timesheet_accounts.accounts_contact !== null
    },
    /**
     * Trigger ms_opts.accounts_contact array reload - from adding a contact in the 'add client contact' modal
     * @returns {boolean|*}
     */
    contact_reload() {
      return this.$store.state.contacts.reload_contacts;
    },
    /*
     * ******************************************
     * Timesheet authorisation
     * ******************************************
     */
    /**
     * If default timesheet authorisation is selected
     * @returns {boolean}
     */
    isDefaultSubmissionType() {
      return this.timesheet_accounts.ts_submission_auth_type === 'Default'
    },
    /**
     * If order specific timesheet authorisation is selected
     * @returns {boolean}
     */
    isOrderSubmissionType() {
      return this.timesheet_accounts.ts_submission_auth_type === 'Order'
    },
    /**
     * if there are authorisers as well as submitters for company default timesheet authorisation
     * @returns {boolean}
     */
    isDefaultSubmissionAuthorisation() {
      let check = false;
      for (let i = 0; i < this.default_timesheet_auth.length; i++) {
        if (this.default_timesheet_auth[i].submission_level >= 2 && this.default_timesheet_auth[i].submission_level <= 7) {
          check = true;
          break;
        }
      }
      return check;
    },
    /**
     * check if there are any timesheet submitters selected for order specific timesheet authorisations
     * @returns {boolean}
     */
    check_submit_ts_contact_null() {
      let check = false;
      for (let i = 0; i < this.ts_auth.submit_ts.length; i++) {
        if ((i + 1) === this.ts_auth.submit_ts.length) {
          if (this.ts_auth.submit_ts[i].contact !== null) {
            check = true;
          }
        }
      }
      return check;
    },
  },
  methods: {
    /**
     * Load data for the component when the step is clicked on in the order creation
     * @returns {Promise<unknown>}
     */
    load_data() {
      return new Promise(load_complete => {
        let load_data_promises = [],
            previous_order_steps_completed = this.$store.state.orders.order_steps.positions_rates.partial_order_complete,
            this_order_step_previously_completed = this.$store.state.orders.order_steps.timesheet_authorisation.partial_order_complete,
            order_company_id = this.$store.state.orders.order_steps.basic_info.data.company.id

        // get company addresses for invoice address multiselect
        load_data_promises.push(new Promise(resolve => {
          axios.get(this.crmApiPath + 'contact_info/address/company/' + order_company_id)
               .then(addresses => {
                 // initialise array that will be set to the multiselect options for the invoice address
                 let temp_addresses = [];
                 for (let i = 0; i < addresses.data.length; i++) {
                   // only add address to multiselect options
                   if (addresses.data[i].address_type_id === 43) {
                     temp_addresses.push(addresses.data[i]);
                   }
                 }
                 this.ms_opts.invoice_address = temp_addresses;
                 // if there is only one accounts address then set the multiselect to this address
                 if (temp_addresses.length === 1) {
                   this.timesheet_accounts.invoice_address = temp_addresses[0];
                 }
                 resolve()
               })
        }))
        // get contacts for accounts & timesheet authorisation multiselect's
        load_data_promises.push(new Promise(resolve => {
          axios.get(this.crmApiPath + 'contact/company/' + order_company_id).then(contacts => {
            this.contacts = contacts.data; // original array of contacts
            this.ms_opts.accounts_contact = contacts.data; // accounts contacts array options
            this.ms_opts.ts_contacts = contacts.data // contacts for order timesheet authorisations
            resolve()
          })
        }))
        // get company default timesheet authorisers
        load_data_promises.push(new Promise(resolve => {
          axios.get(this.crmApiPath + 'timesheet_authorisation/client/get/' + order_company_id)
               .then(ts_auth => {
                 // set company default timesheet authorisers to returned data
                 this.default_timesheet_auth = ts_auth.data
                 // if there are company default timesheet authorisers returned
                 if (this.notEmptyObject(ts_auth.data)) {
                   this.ts_auth.submit_ts = []
                   this.ts_auth.authorising_ts = []
                   for (let i = 0; i < ts_auth.data.length; i++) {
                     if (ts_auth.data[i].submission_level >= 8) {
                       this.ts_auth.submit_ts.push(ts_auth.data[i])
                     }
                     else {
                       this.ts_auth.authorising_ts.push(ts_auth.data[i])
                     }
                   }
                 }
                 // calculate multiselect options for order specific timesheet authorisations
                 this.calc_ts_contacts_in_multiselect()
                 resolve();
               })
        }));

        Promise.all(load_data_promises).then(() => {
          // if this component is being revisited after being saved previously
          if (this_order_step_previously_completed) {
            if (this.$store.state.orders.order_steps.timesheet_authorisation.data.ts_submission_auth_type === 'Order') {
              this.can_watch_auth_type = true
            }
            // load data from vuex store for this component
            this.timesheet_accounts = this.$store.state.orders.order_steps.timesheet_authorisation.data
            // if order specific authorisation
            this.$nextTick(() => {
              if (this.timesheet_accounts.ts_submission_auth_type === 'Order') {
                let sub_ts = [], auth_ts = [];
                // loop through timesheet submitters
                for (let i = 0; i < this.timesheet_accounts.timesheet_auth.submit_ts.length; i++) {
                  let add_sub_ts = {
                    submission_level: i + 8,
                    contact: this.timesheet_accounts.timesheet_auth.submit_ts[i].contact
                  }
                  sub_ts.push(add_sub_ts);
                }
                // loop through timesheet authorisers
                for (let i = 0; i < this.timesheet_accounts.timesheet_auth.authorising_ts.length; i++) {
                  let add_auth_ts = {
                    submission_level: i + 2,
                    contact: this.timesheet_accounts.timesheet_auth.authorising_ts[i].contact
                  }
                  auth_ts.push(add_auth_ts);
                }
                // push it into the timesheet authorisation array to save
                this.ts_auth = {
                  submit_ts: sub_ts,
                  authorising_ts: auth_ts
                };
              }
            })
          }

          // all data calls completed
          load_complete();
        })
      })
    },
    /*
     * ******************************************
     * Invoice address
     * ******************************************
     */
    /**
     * open modal to add a new company address
     */
    add_address_modal() {
      this.$store.commit('SHOW_ADD_ADDRESS_MODAL', {
        id: this.$store.state.orders.order_steps.basic_info.data.company.id,
        company_id: this.$store.state.orders.order_steps.basic_info.data.company.id,
        company_name: this.$store.state.orders.order_steps.basic_info.data.company.company_name
      });
      this.$nextTick(() => this.$root.$emit('bv::show::modal', 'add_address_modal'));
    },
    /*
     * ******************************************
     * Accounts contact
     * ******************************************
     */
    /**
     * return formatted accounts contact full name for accounts contact multiselect
     * @param props
     * @returns {string}
     */
    multiselect_accounts_contact_full_name(props) {
      return props.option.person.first_name + " " + props.option.person.surname
    },
    /**
     * return if an accounts contact has a job title or not for multiselect
     * @param props
     * @returns {boolean}
     */
    has_job_title_multiselect_accounts_contact(props) {
      return props.option.rel_profile.job_title_id !== null
    },
    /**
     * return accounts contact job title for multiselect
     * @param props
     * @returns {string}
     */
    multiselect_accounts_contact_job_title(props) {
      return props.option.rel_profile.job_title.job_title
    },
    /**
     * open modal to add a new company contact
     */
    add_contact() {
      this.$store.commit('SHOW_ADD_CONTACT_MODAL', {
        company_id: this.$store.state.orders.order_steps.basic_info.data.company.id,
        company_name: this.$store.state.orders.order_steps.basic_info.data.company.company_name
      });
      this.$nextTick(() => this.$root.$emit('bv::show::modal', 'add_contact_modal'));
    },
    /*
     * ******************************************
     * Company default Timesheet authorisation
     * ******************************************
     */
    /**
     * Return formatted timesheet authoriser level for company default timesheet authorisers
     * @param ts_as
     * @returns {number}
     */
    default_ts_as_submission_level(ts_as) {
      return ts_as.submission_level - 1
    },
    /**
     * Return formatted contact name for company default timesheet submitters and authorisers
     * @param ts_as
     * @returns {string}
     */
    default_ts_as_contact_full_name(ts_as) {
      return ts_as.contact.person.first_name + " " + ts_as.contact.person.surname
    },
    /**
     * return company default timesheet submitter or authoriser job title (if they have one)
     * @param ts_as
     * @returns {string}
     */
    default_ts_as_contact_job_title(ts_as) {
      return ts_as.contact.rel_profile.job_title_id !== null ? ts_as.contact.rel_profile.job_title.job_title : "";
    },
    /*
     * ******************************************
     * Order specific Timesheet authorisation
     * ******************************************
     */
    /**
     * Calculate available contacts for the order specific timesheet authorisations multiselect's
     */
    calc_ts_contacts_in_multiselect() {
      let passed_contacts = this.contacts;
      for (let i = 0; i < this.ts_auth.submit_ts.length; i++) {
        if (this.ts_auth.submit_ts[i].contact !== null) {
          passed_contacts = passed_contacts.filter(obj => obj.id !== this.ts_auth.submit_ts[i].contact.id)
        }
      }
      for (let i = 0; i < this.ts_auth.authorising_ts.length; i++) {
        if (this.ts_auth.authorising_ts[i].contact !== null) {
          passed_contacts = passed_contacts.filter(obj => obj.id !== this.ts_auth.authorising_ts[i].contact.id)
        }
      }

      this.ms_opts.ts_contacts = [];
      for (let i = 0; i < passed_contacts.length; i++) {
        let job_title = '';
        if ((passed_contacts[i].rel_profile !== null && Object.keys(passed_contacts[i].rel_profile).length > 1) && passed_contacts[i].rel_profile.job_title_id !== null &&
            passed_contacts[i].rel_profile.job_title !== null) {
          job_title = passed_contacts[i].rel_profile.job_title.job_title;
        }
        this.ms_opts.ts_contacts.push({
          company_id: passed_contacts[i].company_id,
          contact_id: passed_contacts[i].id,
          id: passed_contacts[i].id,

          person: {
            first_name: passed_contacts[i].person.first_name,
            surname: passed_contacts[i].person.surname
          },
          rel_profile: {
            job_title_id: passed_contacts[i].rel_profile.job_title_id,
            job_title: {
              job_title: job_title
            }
          }
        })
      }
    },
    /**
     * Add a space for selecting an order specific timesheet submitter
     */
    add_ts_auth_submitting_contact() {
      this.ts_auth.submit_ts.push({contact: null});
      this.calc_ts_contacts_in_multiselect();
    },
    /**
     * Remove a selected contact from the order specific timesheet submitters
     * @param submitting_contact
     * @param index
     * @param id
     */
    remove_ts_auth_submitting_contact(submitting_contact, index, id) {
      this.$snotify.confirm('', '', {
        closeOnClick: false,
        position: 'centerCenter',
        backdrop: 0.5,
        html: '<div class="snotifyToast__title">Confirm Delete Contact</div>' +
            '<div class="snotifyToast__body">' +
            'Are you sure that you wish to remove the client timesheet submitting contact:<br><br>' + submitting_contact +
            '<div class="snotify-icon snotify-icon--confirm"><i class="fas fa-2x fa-exclamation-triangle"></i></div>' +
            '</div>',
        buttons: [
          {
            text: 'Yes', action: (toast) => {
              this.$snotify.remove(toast.id);
              this.ts_auth.submit_ts.splice(index, 1);
              this.$nextTick(() => this.calc_ts_contacts_in_multiselect())
            }
          },
          {text: 'No', action: (toast) => this.$snotify.remove(toast.id)}
        ]
      });
    },
    /**
     * Add a space for selecting an order specific timesheet submitter
     */
    add_ts_auth_authorising_contact() {
      this.ts_auth.authorising_ts.push({contact: null});
      this.calc_ts_contacts_in_multiselect();
    },
    /**
     * Remove a selected contact from the order specific timesheet authorisers
     * @param authorising_contact
     * @param index
     * @param id
     */
    remove_ts_auth_authorising_contact(authorising_contact, index, id) {
      this.$snotify.confirm('', '', {
        closeOnClick: false,
        position: 'centerCenter',
        backdrop: 0.5,
        html: '<div class="snotifyToast__title">Confirm Delete Contact</div>' +
            '<div class="snotifyToast__body">' +
            'Are you sure that you wish to remove the client timesheet authorising contact:<br><br>' + authorising_contact +
            '<div class="snotify-icon snotify-icon--confirm"><i class="fas fa-2x fa-exclamation-triangle"></i></div>' +
            '</div>',
        buttons: [
          {
            text: 'Yes', action: (toast) => {
              this.$snotify.remove(toast.id);
              this.ts_auth.authorising_ts.splice(index, 1);
              this.calc_ts_contacts_in_multiselect();
            }
          },
          {text: 'No', action: (toast) => this.$snotify.remove(toast.id)}
        ]
      });
    },
    /**
     * Selecting a contact for order timesheet authorisers - ensure that the contact is removed from available array of contacts
     */
    select_ts_contact() {
      this.calc_ts_contacts_in_multiselect();
      this.$nextTick(() => {
        if (this.ms_opts.ts_contacts.length === 0) {
          for (let i = 0; i < this.ts_auth.submit_ts.length; i++) {
            this.ts_auth.submit_ts = this.ts_auth.submit_ts.filter(item => item.contact !== null)
          }
          for (let i = 0; i < this.ts_auth.authorising_ts.length; i++) {
            this.ts_auth.authorising_ts = this.ts_auth.authorising_ts.filter(item => item.contact !== null)
          }
        }
      });
    },
    /*
     * ******************************************
     * Save authorisation and accounts information
     * ******************************************
     */
    // submit timesheet authorisation & accounts
    order_timesheets_accounts_submit() {
      this.$refs.orderTimesheetsAccountsForm.validate().then(success => {
        if (!success) {
          this.formErrorsValidationScroll(this.$refs.orderTimesheetsAccountsForm);
          // there are errors with trying to save this step of order creation - store current state of the component into vuex
          this.$store.commit('SAVE_TIMESHEET_AUTHORISATIONS_ERRORS', this.timesheet_accounts);
        }
        else {
          // clear authorisation and submission information for the component
          this.timesheet_accounts.timesheet_auth = [{}];
          // if the authorisation is 'order specific'
          if (this.timesheet_accounts.ts_submission_auth_type === 'Order') {
            let sub_ts = [], auth_ts = [];
            // loop through timesheet submitters
            for (let i = 0; i < this.ts_auth.submit_ts.length; i++) {
              let add_sub_ts = {
                submission_level: i + 8,
                contact: this.ts_auth.submit_ts[i].contact
              }
              sub_ts.push(add_sub_ts);
            }
            // loop through timesheet authorisers
            for (let i = 0; i < this.ts_auth.authorising_ts.length; i++) {
              let add_auth_ts = {
                submission_level: i + 2,
                contact: this.ts_auth.authorising_ts[i].contact
              }
              auth_ts.push(add_auth_ts);
            }
            // push it into the timesheet authorisation array to save
            this.timesheet_accounts.timesheet_auth = {
              submit_ts: sub_ts,
              authorising_ts: auth_ts
            };
          }
          // else if authorisation is 'company default'
          else {
            let sub_ts = [], auth_ts = [];
            for (let i = 0; i < this.default_timesheet_auth.length; i++) {
              if (this.default_timesheet_auth[i].submission_level >= 8) {
                this.default_timesheet_auth[i].contact.submission_level = this.default_timesheet_auth[i].submission_level;
                sub_ts.push(this.default_timesheet_auth[i].contact);
              }
              if (this.default_timesheet_auth[i].submission_level < 8) {
                this.default_timesheet_auth[i].contact.submission_level = this.default_timesheet_auth[i].submission_level;
                auth_ts.push(this.default_timesheet_auth[i].contact);
              }
            }
            this.timesheet_accounts.timesheet_auth.push({
              submit_ts: sub_ts,
              auth_ts: auth_ts
            });
          }
          // commit the status of the component to the vuex store
          this.$store.commit('SAVE_TIMESHEET_AUTHORISATIONS_COMPLETED', this.timesheet_accounts);
        }
      });
    }
  },
  watch: {
    /**
     * Watch trigger value, if the value is true then reload contacts array for both accounts contacts and timesheet authorisation contacts multiselect's
     * @param val
     */
    contact_reload(val) {
      if (val) {
        axios.get(this.crmApiPath + 'contact/company/' + this.$store.state.orders.order_steps.basic_info.data.company.id).then(contacts => {
          this.ms_opts.accounts_contact = contacts.data;
          this.ms_opts.ts_contacts = contacts.data
        })
      }
    },
    /**
     * Watch trigger value, if the value is true then reload the addresses array for the invoice address multiselect
     * @param val
     */
    address_reload(val) {
      if (val) {
        axios.get(this.crmApiPath + 'contact_info/address/company/' + this.$store.state.orders.order_steps.basic_info.data.company.id).then(addresses => {
          let temp_addresses = [];
          for (let i = 0; i < addresses.data.length; i++) {
            if (addresses.data[i].address_type_id === 43) {
              temp_addresses.push(addresses.data[i]);
              if (addresses.data.length === 1) {
                this.timesheet_accounts.invoice_address = addresses.data[i];
              }
            }
          }
          this.ms_opts.invoice_address = temp_addresses;
        });
      }
    },
    /**
     * Watch accounts contact - if the contact selected doesn't have and email OR (landline OR mobile) then show modal to input required information
     * @param contact
     */
    'timesheet_accounts.accounts_contact'(contact) {
      if (contact !== null) {
        // if contact has no email address or if contact has no phone number and no mobile number
        if (contact.emails.work_email === null || (contact.phones.work_landline === null && contact.phones.work_mobile === null)) {
          this.$store.commit('SHOW_EDIT_CONTACT_MODAL', {
            contact_id: contact.id,
            company_id: contact.company_id,
          });
          this.$nextTick(() => this.$root.$emit('bv::show::modal', 'edit_contact_modal'))
        }
      }
    },
    /**
     * When timesheet submission type changes to 'order specific'
     * default to the company default timesheet authorisers setup
     * @param type
     */
    'timesheet_accounts.ts_submission_auth_type'(type) {
      if (type === 'Order' && !this.can_watch_auth_type) {
        // if there are company default timesheet authorisers returned
        if (this.notEmptyObject(this.default_timesheet_auth)) {
          this.ts_auth.submit_ts = []
          this.ts_auth.authorising_ts = []
          for (let i = 0; i < this.default_timesheet_auth.length; i++) {
            if (this.default_timesheet_auth[i].submission_level >= 8) {
              this.ts_auth.submit_ts.push(JSON.parse(JSON.stringify(this.default_timesheet_auth[i])))
            }
            else {
              this.ts_auth.authorising_ts.push(JSON.parse(JSON.stringify(this.default_timesheet_auth[i])))
            }
          }
        }

        // calculate multiselect options for order specific timesheet authorisations
        this.calc_ts_contacts_in_multiselect()
      }
      else if (type === 'Default' && this.can_watch_auth_type) {
        this.can_watch_auth_type = false;
      }
    }
  },
}
</script>

<style scoped lang="scss">
.sm_btn {
  display:     inline-block;
  width:       auto;
  padding:     0 15px;
  line-height: 30px;
  font-size:   0.65rem;
}

/* PRETTY CHECKBOX MULTI LINE */
.pretty {
  white-space:   inherit;
  margin-bottom: 0.5em;

  .state {
    label {
      text-indent:  0;
      padding-left: 2rem;
      line-height:  1;

      &:after, &:before {
        top: 0;
      }
    }
  }
}

.ta_locations {
  margin-bottom: 15px;

  &:last-of-type {
    margin-bottom: 0;
  }

  .ta_location {
    background: $bc_5;
    padding:    10px;
  }
}

</style>
<style lang="scss">
.basic_order_info {
  .multiselect__option {
    height: auto !important;
  }

  .option__desc {
    .option__title {
      font-weight: bold;
      display:     block;
    }

    .option__small {
      font-size: 0.7rem;
      display:   block;
    }
  }
}
</style>
